import {
  useOrganizationsServiceOrganizationsAccessDestroy,
  useOrganizationsServiceOrganizationsAccessListKey,
  useOrganizationsServiceOrganizationsTeamUsersDestroy,
  useOrganizationsServiceOrganizationsTeamUsersListKey,
  useUsersServiceUsersInviteCreate
} from '@organization/api/queries'
import { OrganizationUsers, TeamUsersDetail } from '@organization/api/requests'
import { useUserColumns } from '@organization/features/users/hooks'
import { getTableActions } from '@organization/features/users/utils'

import { paths } from '@organization/router'
import { inviteUrl } from '@organization/utils'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { App, MenuProps, Table, TablePaginationConfig } from 'antd'
import { SorterResult } from 'antd/lib/table/interface'
import { InfoCircle } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { UserRole, UserRoleInTeam } from '../User'

type Data = OrganizationUsers & TeamUsersDetail

type Props = {
  usersData?: Data[]
  isLoading: boolean
  isOnTeamContainer?: boolean
  pagination: TablePaginationConfig
  onChangeSortValue: (key: string) => void
}

export const UsersTable = ({
  usersData,
  isLoading,
  isOnTeamContainer,
  pagination,
  onChangeSortValue
}: Props) => {
  const [currentUser, setCurrentUser] = useState<Data | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isOpenTeamUser, setIsOpenTeamUser] = useState(false)
  const navigate = useNavigate()
  const { invalidateQueries } = useInvalidateQueries()

  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { user: userContext, isAdmin } = useSessionContext()

  const onEdit = (user: Data) => {
    setIsOpen(true)

    if (user) {
      setCurrentUser(user)
    }
  }

  const { mutate: removeUser } =
    useOrganizationsServiceOrganizationsAccessDestroy({
      onSuccess: () => {
        invalidateQueries([useOrganizationsServiceOrganizationsAccessListKey])
      },
      onError: (e) => showApiErrors(e)
    })

  const { mutate: deleteUserFromTeam } =
    useOrganizationsServiceOrganizationsTeamUsersDestroy({
      onSuccess: () => {
        invalidateQueries([
          useOrganizationsServiceOrganizationsTeamUsersListKey
        ])
      },
      onError: (e) => showApiErrors(e)
    })

  const { mutate: reInviteUser } = useUsersServiceUsersInviteCreate({
    onSuccess: () => {
      invalidateQueries([useOrganizationsServiceOrganizationsTeamUsersListKey])
    },
    onError: (e) => showApiErrors(e)
  })

  const onConfirmDeleteTeamUser = (id: number) => {
    modal.confirm({
      title: t('modal.delete_team_user'),
      okText: t('button.delete'),
      okType: 'danger',
      cancelText: t('button.cancel'),
      icon: <InfoCircle />,
      onOk: () => deleteUserFromTeam({ id })
    })
  }

  const onConfirmDeleteUser = (id: number) => {
    modal.confirm({
      title: t('modal.delete_user'),
      okText: t('button.delete'),
      okType: 'danger',
      cancelText: t('button.cancel'),
      icon: <InfoCircle />,
      onOk: () => removeUser({ id })
    })
  }

  const onReInviteUser = (user: Data) => {
    if (!user?.user) return
    modal.confirm({
      title: t('re_invite_confirm'),
      content: t('re_invite_confirm_description'),
      okText: t('re_invite'),
      cancelText: t('button.cancel'),
      icon: <InfoCircle />,
      onOk: () =>
        reInviteUser({
          requestBody: {
            invite_url: inviteUrl,
            organization_id: Number(userContext?.organization?.id),
            users: [
              {
                email: user.user.email,
                identification_number: user.user.identification_number!
              }
            ]
          }
        })
    })
  }

  const buildActionMenu = (user: Data): MenuProps['items'] => {
    if (!isAdmin) return []

    const id = Number(user?.id)
    const isCurrentUser = user?.user?.id === userContext?.user?.id

    const canReinvite = user?.status === 'inactive'

    const tableActions = getTableActions(t)

    if (isOnTeamContainer) {
      return [tableActions.delete(() => onConfirmDeleteTeamUser(id))]
    }

    return [
      tableActions.permissions(() =>
        navigate(paths.users.permissions(String(id)))
      ),
      ...(!isCurrentUser ? [tableActions.edit(() => onEdit(user))] : []),
      ...(canReinvite
        ? [tableActions.reInvite(() => onReInviteUser(user))]
        : []),
      tableActions.delete(() => onConfirmDeleteUser(id))
    ]
  }

  const columns = useUserColumns(buildActionMenu)
  const displayColumns = isAdmin ? columns : columns.slice(0, -1)

  const onTableChange = (
    _: unknown,
    __: unknown,
    sorter: SorterResult<Data> | SorterResult<Data>[]
  ) => {
    const isSingleSorter = (s: typeof sorter): s is SorterResult<Data> =>
      !Array.isArray(s)

    if (isSingleSorter(sorter) && sorter.column?.key && sorter.order) {
      const prefix = sorter.order === 'descend' ? '-' : ''
      onChangeSortValue(`${prefix}${sorter.column.key}`)
    }
  }

  return (
    <>
      <Table
        rowKey="id"
        columns={displayColumns}
        loading={isLoading}
        dataSource={usersData}
        onChange={onTableChange}
        pagination={{
          ...pagination,
          hideOnSinglePage: true
        }}
        showSorterTooltip={false}
      />

      {isOpen && (
        <UserRole
          user={currentUser as OrganizationUsers}
          onCancel={() => setIsOpen(false)}
        />
      )}
      {isOpenTeamUser && (
        <UserRoleInTeam
          user={currentUser as TeamUsersDetail}
          onCancel={() => setIsOpenTeamUser(false)}
        />
      )}
    </>
  )
}
